import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import { XIcon } from "lucide-react";
import { startCase } from "lodash";
import { useId, useRef } from "react";
import { useFormContext, type UseFormRegisterReturn } from "react-hook-form";

interface SimpleSelectFormField {
  label?: string;
  className?: string;
  selectContentClassName?: string;
  /** Pass-through to the wrapping `FormItem` (e.g. `space-y-0` for inline rows). */
  formItemClassName?: string;
  register: UseFormRegisterReturn;
  items: readonly string[] | readonly number[];
  disabledItems?: readonly string[];
  formatItemLabel?: (item: string) => string;
  placeholder?: string;
  disabled?: boolean;
  ref?: React.RefObject<HTMLDivElement>;
  onChange?: (value?: string) => void;
  onOpenChange?: (open: boolean) => void;
  dataTestId?: string;
  clearable?: boolean;
}

export const SimpleSelectFormField = ({
  label,
  register,
  items,
  disabledItems,
  ref,
  placeholder,
  className,
  selectContentClassName,
  formItemClassName,
  disabled,
  onChange,
  onOpenChange,
  formatItemLabel,
  dataTestId,
  clearable = false,
}: SimpleSelectFormField) => {
  const uid = useId();
  const triggerId = `simple-select-${register.name}-${uid}`;

  const stringItems = items.map((item) => item.toString());
  const form = useFormContext();
  // This is needed to allow the select to be disabled when it is inside a disabled fieldset
  const triggerRef = useRef<HTMLButtonElement>(null);
  const isInDisabledFieldset = Boolean(
    triggerRef.current?.closest("fieldset:disabled"),
  );
  return (
    <FormField
      control={form.control}
      {...register}
      // @ts-expect-error This line is to remove a console error
      ref={null}
      render={({ field }) => {
        const stringValue = field.value?.toString();

        return (
          <FormItem data-testid={dataTestId} className={formItemClassName}>
            {label && (
              <div className="flex">
                <FormLabel htmlFor={triggerId}>{label}</FormLabel>
                <FormMessage
                  data-testid="error-message"
                  className="grow text-right"
                />
              </div>
            )}
            <Select
              name={field.name}
              value={stringValue ?? ""}
              onValueChange={(value) => {
                field.onChange(value);
                onChange?.(value);
              }}
              onOpenChange={onOpenChange}
              disabled={isInDisabledFieldset || disabled}
            >
              <FormControl>
                <div className="relative">
                  <SelectTrigger
                    id={triggerId}
                    ref={triggerRef}
                    disabled={isInDisabledFieldset || disabled}
                    className={cn(
                      className,
                      !stringValue && "text-muted-foreground",
                    )}
                    data-testid={dataTestId}
                  >
                    <SelectValue
                      onBlur={field.onBlur}
                      ref={field.ref}
                      placeholder={placeholder ?? ""}
                      className="truncate"
                    />
                  </SelectTrigger>
                  {clearable && stringValue && (
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground absolute top-1/2 right-8 -translate-y-1/2"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setValue(register.name, "", {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                        onChange?.("");
                      }}
                    >
                      <XIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </FormControl>
              <SelectContent ref={ref} className={selectContentClassName}>
                {stringItems.map((item) => (
                  <SelectItem
                    key={item}
                    value={item}
                    disabled={
                      disabledItems ? disabledItems.includes(item) : false
                    }
                  >
                    <span className="block truncate">
                      {formatItemLabel?.(item) ?? startCase(item)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormItem>
        );
      }}
    />
  );
};
